import React, { useCallback, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock, Paperclip } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: pure modules and components, resolvable under Jest without a shim.
import ReadingMarkup from "../../lib/readingMarkup";
import { resolveApiUrl } from "../../pathways/learnerApi";
import { isFlatPath } from "../../pathways/learnerModel";
import { pathwayErrorMessage } from "../../pathways/errors";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";
import StepCourse from "./StepCourse";
import StepLive from "./StepLive";
import StepPractice from "./StepPractice";
import StepQuiz from "./StepQuiz";
import StepVideo from "./StepVideo";

const PRIMARY = "inline-flex h-11 items-center justify-center gap-2 rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream hover:bg-eh-ink/90 disabled:cursor-not-allowed disabled:opacity-50";

/** A file as a card: the paperclip, the file name, opening the gated URL in a new tab. */
function FileCard({ file, testId, download = false }) {
  return (
    <a
      href={resolveApiUrl(file.url)}
      target="_blank"
      rel="noopener noreferrer"
      download={download ? file.filename : undefined}
      data-testid={testId}
      className="flex items-center gap-2 rounded-2xl bg-eh-cream-deep px-4 py-3 text-sm text-eh-ink hover:underline"
    >
      <Paperclip className="h-4 w-4 flex-shrink-0 text-eh-muted" aria-hidden="true" />
      <span className="min-w-0 break-all">{file.filename}</span>
    </a>
  );
}

/**
 * "Level 1 · Step 2 of 4" on a level pathway; "Step 2 of 4" on a successive or alternative
 * one, whose single level is not shown to learners (contract §2). The path type is the
 * screen's own `path_type` (contract §5.4).
 */
function Eyebrow({ screen }) {
  const t = useT("public");
  const { index, total } = screen.position;
  const flat = isFlatPath(screen.path_type);
  return (
    <p data-testid={IDS.stepEyebrow} className="text-xs text-eh-muted">
      {flat
        ? t("pathways.step.eyebrow", { n: index, total })
        : t("pathways.step.eyebrowLevel", { level: screen.level.index, n: index, total })}
    </p>
  );
}

/**
 * One step's screen (spec screen 4, "Step screen"), a PURE presentational view over the
 * contract's `MemberStepScreen` (§5.4). It is also what the practitioner's "Preview as
 * learner" renders (contract §9.1), from `previewModel.toPreviewStepScreen(...)`.
 *
 *   eyebrow        "Level 1 · Step 2 of 4"
 *   title          and "Done" once the step is completed
 *   content        video: the player, "Auto-completes at 90% watched"; when the player
 *                  cannot observe progress, a short explanation and "Mark as done"
 *                  reading: the body (ReadingMarkup), its PDF as a file card, "Mark as done"
 *                  quiz: StepQuiz
 *                  practice: StepPractice: the instructions, then "Mark as done" or (when
 *                    a submission is required) the text/photo form; the learner's own
 *                    submissions with their status and feedback
 *                  live: StepLive: the cohort session's time (browser zone, named) and
 *                    "Add to calendar", or the self-paced session type and "Book this
 *                    session"; nothing here completes it (attendance / a completed booking)
 *                  course: StepCourse: cover, "N of M lessons", "Open course"
 *   resources      downloadable file cards
 *   next           "Next: <title> →", or its locked form when the next step is locked
 *
 * COMPLETION goes through `onComplete()` (the container POSTs `/complete` and re-reads the
 * screen, whose `step.state` then says "completed"). Until that re-read lands, a successful
 * press is remembered here, so the step reads as done at once and cannot be pressed twice.
 *
 * PREVIEW (`preview`): no write and no learner-only link at all. "Mark as done", "Submit
 * answers" and the practice "Submit" are shown disabled, the video's 90 % auto-completion
 * is off, and "Book this session", "Open course" and "Add to calendar" are shown but not
 * followable (contract §10.11). Next and Back still move around.
 *
 * Every file URL is API-relative and is resolved by `learnerApi.resolveApiUrl`, which
 * keeps the tenant's `/api/t/{slug}` prefix on the platform host.
 *
 * @param {object} props
 * @param {object} props.screen    MemberStepScreen
 * @param {() => void} props.onBack
 * @param {(stepId: string) => void} props.onNext
 * @param {() => Promise<any>} props.onComplete
 * @param {(answers: number[]) => Promise<object>} props.onSubmitQuiz
 * @param {(body: { text: string, photo: File|null }) => Promise<object>} [props.onSubmitPractice]
 *        the practice submission (contract §10.3); not needed in preview
 * @param {boolean} [props.preview=false]
 */
export default function StepScreenView({
  screen, onBack, onNext, onComplete, onSubmitQuiz, onSubmitPractice, preview = false,
}) {
  const t = useT("public");
  const tc = useT("common");
  const step = screen.step;
  const [completedHere, setCompletedHere] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The player could not observe progress (StepVideo's onUntracked): completion is manual.
  const [untracked, setUntracked] = useState(false);
  const onUntracked = useCallback(() => setUntracked(true), []);
  const done = step.state === "completed" || completedHere;
  const writable = !preview;

  const complete = async () => {
    if (busy || done || !writable) return;
    setBusy(true);
    setError("");
    try {
      await onComplete();
      setCompletedHere(true);
    } catch (e) {
      setError(pathwayErrorMessage(e, tc, t("pathways.step.completeFailed")));
    } finally {
      setBusy(false);
    }
  };

  // "Mark as done" with its refusal: a reading, and a practice step that needs no submission.
  const markDone = (
    <>
      {error && <p role="alert" data-testid={IDS.stepError} className="text-sm text-rose-700">{error}</p>}
      {!done && (
        <button
          type="button"
          onClick={complete}
          disabled={busy || !writable}
          data-testid={IDS.stepMarkDone}
          className={`${PRIMARY} w-full`}
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {t("pathways.step.markDone")}
        </button>
      )}
    </>
  );

  const next = screen.next;
  const resources = Array.isArray(screen.resources) ? screen.resources : [];

  return (
    <div data-testid={IDS.step} data-step-id={step.id} className="mx-auto w-full max-w-xl space-y-4">
      <button
        type="button"
        onClick={onBack}
        data-testid={IDS.stepBack}
        className="inline-flex items-center gap-2 text-sm text-eh-muted hover:text-eh-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        <span className="break-words">{t("pathways.step.back", { title: screen.pathway_title })}</span>
      </button>

      <article className="rounded-3xl border border-eh-border-soft bg-eh-cream p-5 sm:p-6 space-y-5">
        <header>
          <Eyebrow screen={screen} />
          <h1 className="mt-1 font-display text-3xl text-eh-ink leading-tight break-words">{step.title}</h1>
          {done && (
            <p data-testid={IDS.stepDone} className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-eh-sage-soft px-3 py-1 text-xs font-medium text-eh-ink">
              <Check className="h-3.5 w-3.5" aria-hidden="true" /> {t("pathways.step.done")}
            </p>
          )}
          {preview && <p className="mt-2 text-xs text-eh-muted">{t("pathways.step.preview")}</p>}
        </header>

        {step.kind === "video" && screen.video && (
          <div className="space-y-2">
            <StepVideo
              key={step.id}
              video={screen.video}
              title={step.title}
              tracking={writable && !done}
              onWatched={complete}
              onUntracked={onUntracked}
            />
            {!done && !untracked && (
              <p data-testid={IDS.stepVideoHint} className="text-xs text-eh-muted">{t("pathways.step.autoComplete")}</p>
            )}
            {!done && untracked && (
              <div data-testid={IDS.stepVideoFallback} className="space-y-3">
                <p className="text-sm text-eh-muted">{t("pathways.step.videoUntracked")}</p>
                {error && <p role="alert" data-testid={IDS.stepError} className="text-sm text-rose-700">{error}</p>}
                <button
                  type="button"
                  onClick={complete}
                  disabled={busy || !writable}
                  data-testid={IDS.stepMarkDone}
                  className={`${PRIMARY} w-full`}
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {t("pathways.step.markDone")}
                </button>
              </div>
            )}
            {error && !busy && !untracked && (
              <div className="space-y-2">
                <p role="alert" data-testid={IDS.stepError} className="text-sm text-rose-700">{error}</p>
                <button type="button" onClick={complete} className={PRIMARY}>{t("pathways.step.retrySave")}</button>
              </div>
            )}
          </div>
        )}

        {step.kind === "reading" && screen.reading && (
          <div className="space-y-4">
            {screen.reading.body && screen.reading.body.trim() && (
              <div className="rounded-2xl bg-eh-surface px-4 py-4">
                <ReadingMarkup body={screen.reading.body} testId={IDS.stepReading} className="text-sm text-eh-ink" />
              </div>
            )}
            {screen.reading.pdf && <FileCard file={screen.reading.pdf} testId={IDS.stepPdf} />}
            {markDone}
          </div>
        )}

        {step.kind === "practice" && screen.practice && (
          <StepPractice
            key={step.id}
            practice={screen.practice}
            markDone={markDone}
            onSubmit={onSubmitPractice}
            disabled={!writable}
          />
        )}

        {step.kind === "live" && screen.live && <StepLive live={screen.live} done={done} disabled={!writable} />}

        {step.kind === "course" && screen.course && <StepCourse course={screen.course} done={done} disabled={!writable} />}

        {step.kind === "quiz" && screen.quiz && (
          <StepQuiz key={step.id} quiz={screen.quiz} onSubmitQuiz={onSubmitQuiz} disabled={!writable} />
        )}

        {resources.length > 0 && (
          <section data-testid={IDS.stepResources} aria-labelledby="pathway-step-resources" className="space-y-2">
            <h2 id="pathway-step-resources" className="text-sm font-semibold text-eh-ink">{t("pathways.step.resources")}</h2>
            <ul className="space-y-2">
              {resources.map((file, i) => (
                // Resources carry no id on the learner wire; their position is their identity.
                <li key={`${i}-${file.url}`}><FileCard file={file} testId={IDS.stepResource(i)} download /></li>
              ))}
            </ul>
          </section>
        )}

        {next && (next.state === "locked" ? (
          <p data-testid={IDS.stepNext} data-locked="true" aria-disabled="true" className="inline-flex items-center gap-1.5 text-sm text-eh-muted">
            <Lock className="h-4 w-4" aria-hidden="true" />
            <span className="break-words">{t("pathways.step.nextLocked", { title: next.title })}</span>
          </p>
        ) : (
          <button
            type="button"
            onClick={() => onNext(next.id)}
            data-testid={IDS.stepNext}
            data-locked="false"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-eh-ink hover:underline"
          >
            <span className="break-words">{t("pathways.step.next", { title: next.title })}</span>
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        ))}
      </article>
    </div>
  );
}
